import { useEffect, useState } from 'react';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { AlertService } from '@src/api/common/AlertService';
import { AlertVariantType } from '@src/constants/AlertVariantType';
import { UserService } from '@src/api/common/UserService';
import { AzureLoginHint } from '@src/constants/BaseUrls';
import {
  acquireTokenSilently,
  hasMicrosoftAccount,
  initializeMsal,
  isInteractionRequiredError,
  loginWithMicrosoft,
  trySilentSingleSignOn,
} from '@src/api/common/MsalService';

//Below is an example of how to call an API service
//import { AuthenticationService } from '@src/api/web-services/public/AuthenticationService';

const LoginController = () => {
  const { navigate } = useRouter();
  const { AlertModel, ShowAlert, HideAlert } = AlertService();
  const preferredLoginHint =
    UserService.GetLoginHint() || AzureLoginHint || null;
  const [isInitializing, setIsInitializing] = useState(true);

  const buildInitials = (name: string, fallbackDisplayName?: string) => {
    const source = name || fallbackDisplayName || '';
    return (
      source
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || '')
        .join('') || 'US'
    );
  };

  const extractUserFromMsal = (authResult: any) => {
    const claims = (authResult?.account?.idTokenClaims || {}) as Record<
      string,
      unknown
    >;
    const displayName =
      (typeof authResult?.account?.name === 'string' &&
        authResult.account.name) ||
      (typeof claims.name === 'string' && claims.name) ||
      'User';
    const givenName =
      (typeof claims.given_name === 'string' && claims.given_name) ||
      (typeof claims.name === 'string' && claims.name.split(' ')[0]) ||
      displayName;
    const emailAddress =
      (typeof claims.preferred_username === 'string' &&
        claims.preferred_username) ||
      (typeof claims.upn === 'string' && claims.upn) ||
      (typeof authResult?.account?.username === 'string' &&
        authResult.account.username) ||
      '';

    return {
      displayName,
      givenName,
      emailAddress,
      initials: buildInitials(givenName, displayName),
      userName: emailAddress,
    };
  };

  // Helper: save user identity and store an API-scoped token in the cookie.
  // The login result's accessToken is always a Graph token (aud: 00000003...) —
  // we must call acquireTokenSilently separately to get the right audience for Flask.
  const saveUserWithApiToken = async (authResult: any) => {
    const user = extractUserFromMsal(authResult);
    let accessToken: string | undefined;
    try {
      const apiTokenResult = await acquireTokenSilently(false);
      accessToken = apiTokenResult?.accessToken;
    } catch {
      // fall through to idToken fallback
    }
    // idToken aud = clientId — acceptable fallback when silent refresh unavailable
    if (!accessToken) {
      accessToken = authResult?.idToken;
    }
    UserService.SetUser({ user, accessToken });
  };

  const startSeamlessMsalLogin = async () => {
    const redirectResult = await initializeMsal();

    if (redirectResult?.account) {
      await saveUserWithApiToken(redirectResult);
      navigate('/');
      return;
    }

    if (hasMicrosoftAccount()) {
      const silentTokenResult = await acquireTokenSilently(false);

      if (silentTokenResult?.account) {
        await saveUserWithApiToken(silentTokenResult);
        navigate('/');
        return;
      }
    }

    const seamlessResult = await trySilentSingleSignOn(preferredLoginHint);
    if (seamlessResult?.account) {
      await saveUserWithApiToken(seamlessResult);
      navigate('/');
      return;
    }

    await loginWithMicrosoft(preferredLoginHint);
  };

  const LoginPost = async () => {
    await HideAlert();
    try {
      await startSeamlessMsalLogin();
    } catch (error: any) {
      if (isInteractionRequiredError(error)) {
        await loginWithMicrosoft(preferredLoginHint);
        return;
      }

      AlertModel.variant = AlertVariantType.ERROR;
      AlertModel.message =
        error?.message || 'Unable to complete seamless Microsoft sign-in.';
      AlertModel.visible = true;
      await ShowAlert(AlertModel);
    }
  };

  const SignUpClick = async () => {
    await HideAlert();
    await LoginPost();
  };

  useEffect(() => {
    const initLoginScreen = async () => {
      await HideAlert();

      // After explicit app logout, render login immediately without auto-SSO attempts.
      if (UserService.ConsumeSkipAutoSsoOnce()) {
        setIsInitializing(false);
        return;
      }

      if (UserService.IsLoggedIn()) {
        navigate('/');
        return;
      }

      setIsInitializing(false);
    };

    void initLoginScreen();
  }, []);

  return [{ LoginPost, SignUpClick, isInitializing }];
};

export { LoginController };
